import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useIntl } from 'react-intl';
import { parseAxSelector } from '../../../data/projects/artifacts/ax-selector-label';
import { LabelledContextPanel } from '../../inline-approval/labelled-context-panel';
import type { NotePushApprovalRow } from './ApprovalRequestBlock.display';

/**
 * Clinician-friendly body for a note-push write HITL approval. Replaces the
 * redacted `tool_input` JSON with PER-EMR-FIELD rows read from the server-authored
 * `context.approval_display` — one row per unique target field showing the field,
 * the Heidi sections that combine into it, and the EXACT text the child will
 * write. Server-derived so the write plan the clinician approves can't diverge
 * from what executes (and can't be bypassed by auto-approval).
 */
export interface NotePushApprovalDetailsProps {
  rows: NotePushApprovalRow[];
}

/** Best-effort human label for a selector JSON string (its `name`, else `role`). */
const selectorLabel = (selector: string): string => {
  const parsed = parseAxSelector(selector);
  return parsed?.name ?? parsed?.role ?? selector;
};

export const NotePushApprovalDetails = ({ rows }: NotePushApprovalDetailsProps) => {
  const intl = useIntl();
  return (
    <LabelledContextPanel
      label={intl.formatMessage({
        defaultMessage: 'Will write to the chart',
        id: 'Fgr74sr+B0',
        description: 'Note-push approval card: label above the per-field write preview',
      })}
    >
      <ul className="flex flex-col gap-3">
        {rows.map((row, index) => (
          <li key={`${index}-${row.selector}`} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              <TypographyP2 className="font-medium">
                {selectorLabel(row.selector)}
              </TypographyP2>
              {row.section_headings.length > 0 && (
                <TypographyCaption className="text-text-secondary">
                  {intl.formatMessage(
                    {
                      defaultMessage: 'from {sections}',
                      id: 'CLLMYSFTn9',
                      description:
                        'Note-push approval card: the Heidi sections that combine into a field',
                    },
                    { sections: row.section_headings.join(', ') }
                  )}
                </TypographyCaption>
              )}
            </div>
            <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-surface-hover p-2 font-mono text-[11px] leading-snug text-text-secondary">
              {row.prepared_input}
            </pre>
          </li>
        ))}
      </ul>
    </LabelledContextPanel>
  );
};
